import {isInventoryOwner} from '../utils/inventoryNavigation.mjs';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Page, PageHeader } from 'design';
import './SlabVaultLegacy.css';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.accessToken ? p : null;
  } catch { return null; }
}

function fmt(n) {
  const v = parseFloat(n);
  if (!v && v !== 0) return '—';
  return '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function SlabVaultLegacy({ canonicalReceiving = false }) {
  const session = getStoredUser();
  const email = (session?.email || '').toLowerCase();
  const inventoryOwner = isInventoryOwner(session);
  const allowed = email.endsWith('@pokerand.net') || email.endsWith('@randcards.com');

  const [slabs, setSlabs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [sortMode, setSortMode] = useState('newest');
  const [viewMode, setViewMode] = useState('collections');
  const [zoomImg, setZoomImg] = useState(null);
  const [addForm, setAddForm] = useState({ cert_number: '', purchase_price: '', purchase_date: '', notes: '' });
  const [addStatus, setAddStatus] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!session || !allowed) return;

    const controller = new AbortController();

    fetch('/api/slab-vault/slabs', {
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${session.accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setSlabs(Array.isArray(data?.slabs) ? data.slabs : []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load slabs');
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!session || !allowed) return;

    const controller = new AbortController();
    setAnalysisLoading(true);

    fetch('/api/slab-vault/collections', {
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${session.accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (data?.collections) setAnalysis(data);
        setAnalysisLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setAnalysisLoading(false);
      });

    return () => controller.abort();
  }, []);

  if (!session) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/" replace />;

  async function addSlab(e) {
    e.preventDefault();
    const certNumber = addForm.cert_number.trim();
    if (!certNumber || adding) return;

    setAdding(true);
    setAddStatus(null);

    try {
      const res = await fetch('/api/slab-vault/add', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cert_number: certNumber,
          purchase_price: addForm.purchase_price,
          purchase_date: addForm.purchase_date,
          notes: addForm.notes,
        }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(body.error || body.message || `HTTP ${res.status}`);
      }

      const matchText = body.purchase_match
        ? ` Matched eBay: £${Number(body.purchase_match.purchase_price || 0).toLocaleString('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} from ${body.purchase_match.seller || 'seller'} on ${body.purchase_match.purchase_date || 'unknown date'}.`
        : '';

      setSlabs((current) => [body.slab, ...current]);
      setAddForm({ cert_number: '', purchase_price: '', purchase_date: '', notes: '' });
      setAddStatus({ type: 'success', text: `Added ${body.slab?.card_name || certNumber}.${matchText || ' Checking eBay cost in the background.'}` });

      if (body.slab?.id && !addForm.purchase_price) {
        matchPurchase(body.slab);
      }
    } catch (err) {
      setAddStatus({ type: 'error', text: err.message || 'Failed to add slab' });
    } finally {
      setAdding(false);
    }
  }

  async function matchPurchase(slab) {
    try {
      const res = await fetch('/api/slab-vault/match-purchase', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: slab.id }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok || !body.slab) {
        return;
      }

      setSlabs((current) => current.map((item) => (item.id === body.slab.id ? body.slab : item)));

      if (body.purchase_match) {
        setAddStatus({
          type: 'success',
          text: `Added ${body.slab.card_name || 'slab'}. Matched eBay: £${Number(
            body.purchase_match.purchase_price || 0
          ).toLocaleString('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} from ${body.purchase_match.seller || 'seller'} on ${body.purchase_match.purchase_date || 'unknown date'}.`,
        });
      }
    } catch {
      // Purchase matching is best-effort; the slab has already been added.
    }
  }

  async function removeSlab(slab) {
    if (!slab?.id || deletingId) return;

    const label = [slab.card_name, slab.set_name, slab.cert_number ? `Cert ${slab.cert_number}` : null]
      .filter(Boolean)
      .join(' · ');
    const confirmed = window.confirm(`Remove ${label} from the slab vault?`);
    if (!confirmed) return;

    setDeletingId(slab.id);
    setAddStatus(null);

    try {
      const res = await fetch('/api/slab-vault/remove', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: slab.id }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(body.error || body.message || `HTTP ${res.status}`);
      }

      setSlabs((current) => current.filter((item) => item.id !== slab.id));
      setAddStatus({ type: 'success', text: `Removed ${label}.` });
    } catch (err) {
      setAddStatus({ type: 'error', text: err.message || 'Failed to remove slab' });
    } finally {
      setDeletingId(null);
    }
  }

  const categoryCounts = slabs.reduce((acc, slab) => {
    if (!slab.category) return acc;
    acc[slab.category] = (acc[slab.category] || 0) + 1;
    return acc;
  }, {});
  const categories = Object.keys(categoryCounts).sort((a, b) => {
    const countDiff = categoryCounts[b] - categoryCounts[a];
    return countDiff || a.localeCompare(b);
  });
  const selectedCollection = analysis?.collections?.find((collection) => collection.id === collectionFilter) || null;
  const collectionSlabIds = selectedCollection ? new Set(selectedCollection.slab_ids || []) : null;
  const filtered = slabs.filter((s) => {
    const query = search.toLowerCase();
    const matchesSearch = !query || [
      s.card_name,
      s.set_name,
      s.cert_number,
      s.card_number,
      s.category,
    ].some((value) => String(value || '').toLowerCase().includes(query));
    const matchesCategory = !categoryFilter || s.category === categoryFilter;
    const matchesCollection = !collectionSlabIds || collectionSlabIds.has(s.id);
    return matchesSearch && matchesCategory && matchesCollection;
  });
  const sortedSlabs = [...filtered].sort((a, b) => {
    const valueA = parseFloat(a.current_value) || 0;
    const valueB = parseFloat(b.current_value) || 0;
    const costA = parseFloat(a.purchase_price) || 0;
    const costB = parseFloat(b.purchase_price) || 0;

    if (sortMode === 'value-desc') return valueB - valueA;
    if (sortMode === 'value-asc') return valueA - valueB;
    if (sortMode === 'pnl-desc') return (valueB - costB) - (valueA - costA);
    if (sortMode === 'pnl-asc') return (valueA - costA) - (valueB - costB);

    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

  const totalValue = slabs.reduce((sum, s) => sum + (parseFloat(s.current_value) || 0), 0);
  const totalCost = slabs.reduce((sum, s) => sum + (parseFloat(s.purchase_price) || 0), 0);
  const pnl = totalValue - totalCost;

  return (
    <Page className="slv-page">
      {zoomImg && (
        <div className="slv-lightbox" onClick={() => setZoomImg(null)}>
          <img src={zoomImg} alt="Slab" className="slv-lightbox__img" />
        </div>
      )}
      <PageHeader
        title="Slab Vault"
        description={<>Randy’s PC · company-owned collection — {slabs.length} slabs tracked</>}
      />

      {error && <div className="rc-alert rc-alert--error">⚠️ {error}</div>}
      {addStatus && (
        <div className={addStatus.type === 'success' ? 'rc-alert rc-alert--success' : 'rc-alert rc-alert--error'}>
          {addStatus.text}
        </div>
      )}

      {!loading && !error && (
        <div className="rc-stats">
          <div className="rc-stat">
            <span className="rc-stat__label">Current Value</span>
            <span className="rc-stat__value">{fmt(totalValue)}</span>
          </div>
          <div className="rc-stat">
            <span className="rc-stat__label">Invested</span>
            <span className="rc-stat__value">{fmt(totalCost)}</span>
          </div>
          <div className="rc-stat">
            <span className="rc-stat__label">P&amp;L</span>
            <span className={`rc-stat__value ${pnl >= 0 ? 'slv-gain' : 'slv-loss'}`}>
              {pnl >= 0 ? '+' : ''}{fmt(pnl)}
            </span>
          </div>
          <div className="rc-stat">
            <span className="rc-stat__label">Slabs</span>
            <span className="rc-stat__value">{slabs.length}</span>
          </div>
        </div>
      )}

      {!loading && !error && canonicalReceiving ? (inventoryOwner ? <div className="slv-receive"><a href="/slab-inventory?use=randys_pc" className="rc-btn rc-btn--primary">Receive or manage PC slabs in inventory</a></div> : null) : !loading && !error && (
        <form className="rc-panel slv-add" onSubmit={addSlab}>
          <div className="slv-add__grid">
            <label className="rc-field">
              <span className="rc-label">ACE Cert</span>
              <input
                className="slv-cert-input"
                inputMode="numeric"
                placeholder="e.g. 687970"
                value={addForm.cert_number}
                onChange={(e) => setAddForm({ ...addForm, cert_number: e.target.value })}
              />
            </label>
            <label className="rc-field">
              <span className="rc-label">Cost</span>
              <input
                inputMode="decimal"
                placeholder="Optional"
                value={addForm.purchase_price}
                onChange={(e) => setAddForm({ ...addForm, purchase_price: e.target.value })}
              />
            </label>
            <label className="rc-field">
              <span className="rc-label">Bought</span>
              <input
                type="date"
                value={addForm.purchase_date}
                onChange={(e) => setAddForm({ ...addForm, purchase_date: e.target.value })}
              />
            </label>
            <label className="rc-field">
              <span className="rc-label">Notes</span>
              <input
                placeholder="Optional"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
              />
            </label>
            <button type="submit" className="rc-btn rc-btn--primary" disabled={adding || !addForm.cert_number.trim()}>
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {!loading && !error && (
        <div className="rc-tabs slv-tabs">
          {[
            ['collections', 'Collections'],
            ['gallery', 'Gallery'],
            ['list', 'List'],
            ['gaps', 'Gaps'],
          ].map(([mode, label]) => (
            <button key={mode} type="button" className={viewMode === mode ? 'rc-tab is-active' : 'rc-tab'} onClick={() => setViewMode(mode)}>
              {label}
            </button>
          ))}
        </div>
      )}

      {!loading && !error && viewMode === 'collections' && (
        <section className="slv-section">
          <div className="slv-section__head">
            <h2 className="rc-eyebrow">Collection Lanes</h2>
            <p className="slv-note">
              {analysisLoading && !analysis ? 'Studying ACE data...' : 'Click a lane to browse it. Missing targets are buy-list prompts, not cert guarantees.'}
            </p>
          </div>
          <div className="slv-lanes">
            {(analysis?.collections || []).map((collection) => (
              <button
                key={collection.id}
                type="button"
                className="rc-tile slv-lane"
                data-active={collectionFilter === collection.id || undefined}
                onClick={() => setCollectionFilter(collectionFilter === collection.id ? '' : collection.id)}
              >
                <span className="slv-lane__title">{collection.title}</span>
                <span className="slv-lane__text">{collection.description}</span>
                <span className="slv-lane__figures">
                  <span className="rc-badge">{collection.owned_count} owned</span>
                  {collection.completion_pct !== null && (
                    <span className="rc-badge">{collection.completion_pct}% target run</span>
                  )}
                  <span className="slv-lane__value">{fmt(collection.total_value)}</span>
                </span>
                {collection.missing_targets?.length > 0 && (
                  <span className="slv-lane__next">
                    Next: {collection.missing_targets.slice(0, 3).map((target) => target.card).join(' | ')}
                  </span>
                )}
              </button>
            ))}
          </div>

          {selectedCollection && (
            <div className="slv-gaps">
              <h3 className="slv-gaps__title">{selectedCollection.title} gaps</h3>
              <div className="slv-targets">
                {selectedCollection.missing_targets.map((target) => (
                  <div key={target.card + '-' + target.set + '-' + target.number} className="slv-target">
                    <div className="slv-target__card">{target.card}</div>
                    <div className="slv-target__meta">{target.set} {target.number ? '· ' + target.number : ''}</div>
                    <div className="slv-target__why">{target.priority} · {target.why}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {!loading && !error && viewMode === 'gaps' && (
        <section className="slv-section">
          <div className="slv-section__head">
            <h2 className="rc-eyebrow">Set Gaps From ACE Database</h2>
            <p className="slv-note">
              Based on sets you already own heavily. Completion is by card number where ACE has lookup data.
            </p>
          </div>
          <div className="slv-lanes slv-sets">
            {(analysis?.set_gaps || []).filter((gap) => gap.missing_chase?.length).map((gap) => (
              <div key={gap.set_name} className="slv-set">
                <div className="slv-lane__title">{gap.set_name}</div>
                <div className="slv-lane__text">
                  {gap.owned_count} owned
                  {gap.completion_pct !== null ? ' · ' + gap.completion_pct + '% by number' : ''}
                </div>
                <div className="slv-targets">
                  {gap.missing_chase.slice(0, 4).map((target) => (
                    <div key={gap.set_name + '-' + target.card + '-' + target.number} className="slv-target">
                      <div className="slv-target__card">{target.card}</div>
                      <div className="slv-target__meta">
                        {target.number || 'No number'} · ACE 10 pop {target.grade_10_pop || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!loading && !error && categories.length > 0 && (
        <section className="slv-categories">
          <h2 className="rc-label">Categories</h2>
          <div className="slv-chips">
            <button
              type="button"
              className={!categoryFilter ? 'rc-chip is-active' : 'rc-chip'}
              onClick={() => setCategoryFilter('')}
            >
              All <span className="rc-chip__count">{slabs.length}</span>
            </button>
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={categoryFilter === category ? 'rc-chip is-active' : 'rc-chip'}
                onClick={() => setCategoryFilter(category)}
              >
                {category} <span className="rc-chip__count">{categoryCounts[category]}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!loading && (
        <div className="slv-filters">
          <label className="rc-input slv-search">
            <MagnifyingGlass size={18} aria-hidden="true" />
            <input
              placeholder="Search by card, set, cert, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
          >
            <option value="newest">Newest added</option>
            <option value="value-desc">Worth: high to low</option>
            <option value="value-asc">Worth: low to high</option>
            <option value="pnl-desc">P&amp;L: high to low</option>
            <option value="pnl-asc">P&amp;L: low to high</option>
          </select>
        </div>
      )}

      {loading && (
        <div className="slv-loading">
          <p>Loading vault…</p>
          <div className="slv-loading__rows" aria-hidden="true">
            {[0, 1, 2].map((row) => (
              <span key={row} className="slv-loading__row">
                <span className="rc-skeleton slv-loading__thumb" />
                <span className="slv-loading__lines">
                  <span className="rc-skeleton rc-skeleton--line" style={{ width: `${56 - row * 8}%` }} />
                  <span className="rc-skeleton rc-skeleton--line" style={{ width: `${32 - row * 4}%` }} />
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'gallery' && (
        <div className="slv-gallery">
          {sortedSlabs.map((slab) => {
            const imgUrl = slab.cert_number
              ? 'https://collectible-images.ams3.cdn.digitaloceanspaces.com/public/collectible/' + slab.cert_number + '/' + slab.cert_number + '_front.webp'
              : null;
            return (
              <div key={slab.id} className="slv-card">
                <div className="slv-card__stage">
                  {imgUrl && (
                    <img
                      src={imgUrl}
                      alt={slab.card_name || 'Slab'}
                      className="slv-card__img"
                      onClick={() => setZoomImg(imgUrl)}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="slv-card__name">{slab.card_name || '—'}</div>
                <div className="slv-card__set">{slab.set_name}</div>
                <div className="slv-card__foot">
                  <span className="rc-badge">{slab.grade || 'ACE'}</span>
                  <span className="slv-card__value">{fmt(slab.current_value)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode !== 'gallery' && viewMode !== 'gaps' && (
      <div className="slv-list">
        {sortedSlabs.map((slab) => {
          const imgUrl = slab.cert_number
            ? `https://collectible-images.ams3.cdn.digitaloceanspaces.com/public/collectible/${slab.cert_number}/${slab.cert_number}_front.webp`
            : null;
          return (
            <div key={slab.id} className="slv-row">
              {imgUrl && (
                <img
                  src={imgUrl}
                  alt={slab.card_name || 'Slab'}
                  className="slv-row__img"
                  onClick={() => setZoomImg(imgUrl)}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            <div className="slv-row__body">
              <div className="slv-row__main">
                <div className="slv-row__name">{slab.card_name || '—'}</div>
                <div className="slv-row__meta">
                  {slab.set_name} {slab.cert_number && <>· Cert <span className="slv-mono">{slab.cert_number}</span></>}
                </div>
                {slab.category && (
                  <div className="slv-row__category">{slab.category}</div>
                )}
              </div>
              <div className="slv-row__end">
                {slab.grade && (
                  <span className="rc-badge slv-row__grade">
                    {String(slab.grade).toUpperCase().startsWith('ACE')
                      ? slab.grade
                      : `${slab.grader || 'ACE'} ${slab.grade}`}
                  </span>
                )}
                <div className="slv-row__price">
                  <div className="slv-row__value">{fmt(slab.current_value)}</div>
                  <div className="slv-row__cost">Cost {fmt(slab.purchase_price)}</div>
                </div>
                {slab.ownership === 'business' ? (inventoryOwner ? <a href={`/slab-inventory`} className="rc-btn rc-btn--sm">Manage in inventory</a> : null) : <button
                  type="button"
                  className="rc-btn rc-btn--danger rc-btn--sm"
                  disabled={deletingId === slab.id}
                  onClick={() => removeSlab(slab)}
                >
                  {deletingId === slab.id ? 'Removing...' : 'Remove'}
                </button>}
              </div>
            </div>
          </div>
          );
        })}

        {!loading && sortedSlabs.length === 0 && !error && (
          <div className="slv-empty">
            {search ? 'No slabs match your search.' : 'No slabs in the vault yet.'}
          </div>
        )}
      </div>
      )}
    </Page>
  );
}
